import React, { FC, useMemo, useState } from 'react';
import { LuSearch, LuExternalLink, LuShieldCheck } from 'react-icons/lu';
import { FaGooglePlay } from 'react-icons/fa6';
import SectionTitle from './SectionTitle';
import AppIcon from './AppIcon';
import { apps, categories, iconUrl, slugsWithIcon, playLink, policyLink, AppItem, Category } from '../data/apps';
import { profile } from '../data/profile';

type Filter = 'Todos' | Category;
const hue: Record<Category, string> = {
  'Jogos & Quiz': 'var(--c-games)',
  Utilidades: 'var(--c-utils)',
  'Bem-estar': 'var(--c-well)',
  Negócios: 'var(--c-biz)',
};

const AppThumb: FC<{ app: AppItem }> = ({ app }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className="app__thumb" style={{ ['--hue' as string]: hue[app.category] }}>
      {failed || !slugsWithIcon.includes(app.slug) ? (
        <AppIcon name={app.icon} size={26} />
      ) : (
        <img src={iconUrl(app)} alt="" width={56} height={56} loading="lazy" onError={() => setFailed(true)} />
      )}
    </span>
  );
};

const Apps: FC = () => {
  const [filter, setFilter] = useState<Filter>('Todos');
  const [q, setQ] = useState('');

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return apps.filter(
      (a) =>
        (filter === 'Todos' || a.category === filter) &&
        (!term || a.name.toLowerCase().includes(term) || a.description.toLowerCase().includes(term))
    );
  }, [filter, q]);

  const count = (c: Filter) => (c === 'Todos' ? apps.length : apps.filter((a) => a.category === c).length);

  return (
    <section id="apps" className="section">
      <div className="container">
        <SectionTitle
          eyebrow="Portfólio mobile"
          title={`${apps.length} apps publicados na Google Play`}
          subtitle="Jogos, utilidades e soluções para negócios, todos desenvolvidos e mantidos por mim."
        />

        <div className="toolbar reveal">
          <div className="filters" role="tablist" aria-label="Filtrar por categoria">
            {(['Todos', ...categories] as Filter[]).map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={filter === c}
                className={`pill ${filter === c ? 'is-active' : ''}`}
                onClick={() => setFilter(c)}
              >
                {c} <span className="pill__n">{count(c)}</span>
              </button>
            ))}
          </div>
          <label className="search">
            <LuSearch size={16} aria-hidden="true" />
            <input type="search" placeholder="Buscar app…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar app" />
          </label>
        </div>

        {list.length === 0 ? (
          <p className="empty">Nenhum app encontrado para “{q}”.</p>
        ) : (
          <div className="grid grid--3">
            {list.map((app) => (
              <article key={app.slug} className="card app">
                <div className="app__head">
                  <AppThumb app={app} />
                  <div>
                    <h3>{app.name}</h3>
                    <span className="tag" style={{ ['--hue' as string]: hue[app.category] }}>{app.category}</span>
                  </div>
                </div>
                <p>{app.description}</p>
                <div className="app__actions">
                  <a className="btn btn--sm btn--primary" href={playLink(app)} target="_blank" rel="noreferrer">
                    <FaGooglePlay size={13} /> Play Store
                  </a>
                  <a className="btn btn--sm btn--ghost" href={policyLink(app)} target="_blank" rel="noreferrer">
                    <LuShieldCheck size={14} /> Política
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="center reveal">
          <a className="btn btn--ghost" href={profile.playStore} target="_blank" rel="noreferrer">
            Ver todos na Google Play <LuExternalLink size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Apps;
